<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">
        {{ editing ? "Editar colaborador" : "Nuevo colaborador" }}
      </h1>
    </div>

    <form @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del colaborador' : 'Datos del nuevo colaborador'" :body-padding="false">

        <wz-tabs :items="tabItems" v-model="activeTab" variant="underline" style="padding: 0 20px;">

          <!-- Tab: Datos personales -->
          <template #personal>
            <PersonalDataTab
              :form="form"
              :v="$v.form"
              :districts="districts"
              :nationalityList="nationalityList"
              :identifierTypes="identifierTypes"
              :genderList="genderList"
            />
          </template>

          <!-- Tab: Contacto y domicilio -->
          <template #contact>
            <ContactTab
              :form="form"
              :v="$v.form"
              :districts="districts"
            />
          </template>

          <!-- Tab: Banco y pensiones -->
          <template #bank>
            <BankAndPensionsTab
              :form="form"
              :v="$v.form"
              :banks="banks"
              :pensionEntities="pensionEntities"
              :employeeId="employeeId"
            />
          </template>

          <!-- Tab: Académico -->
          <template #academic>
            <AcademicTab
              :form="form"
              :v="$v.form"
            />
          </template>

          <!-- Tab: Familia — alta: borrador en memoria; edición: CRUD en vivo (mismo que el detalle) -->
          <template #family>
            <FamilyDraftTab v-if="isAlta" v-model="familyDependents" />
            <FamilyTab
              v-else-if="employeeId"
              :employee-id="employeeId"
              :employee-paternal-surname="form.lastNamePaternal || ''"
              :employee-gender="form.gender"
            />
          </template>

          <!-- Tab: Documentos — alta: buffer en memoria; edición: CRUD en vivo (mismo que el detalle) -->
          <template #documents>
            <DocumentsDraftTab v-if="isAlta" v-model="documentsDraft" />
            <DocumentsTab v-else-if="employeeId" :employee-id="employeeId" />
          </template>

          <!-- Tab: Campos personalizados (CDEF010) — solo en edición -->
          <template #custom>
            <div class="py-3">
              <custom-fields-section v-if="employeeId" entity="COLABORADOR" :entity-id="employeeId" />
            </div>
          </template>

        </wz-tabs>

        <div class="flex justify-end gap-2 mt-3 px-4 pb-3">
          <wz-button
            variant="light"
            @click="$router.push(editing ? { name: 'employee-details', params: { id: $route.params.id } } : { name: 'employee-list' })"
          >
            {{ editing ? "Volver" : "Cancelar" }}
          </wz-button>
          <wz-button type="submit" variant="primary" class="ml-2" :disabled="submitting">
            {{ saveLabel }}
          </wz-button>
        </div>

      </wz-card>
    </form>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { CustomValidators } from "@wizepe/wizeui"
import { email, numeric, required, requiredIf, minLength } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

import {
  EmployeeService,
  EmployeeDocumentService,
  UbigeoService,
  CountryService,
  BankService,
  PensionEntityService,
} from '@/core/services'
import { Constants } from '@/core/utils'

import PersonalDataTab    from './tabs/PersonalDataTab.vue'
import ContactTab         from './tabs/ContactTab.vue'
import BankAndPensionsTab from './tabs/BankAndPensionsTab.vue'
import AcademicTab        from './tabs/AcademicTab.vue'
import FamilyDraftTab     from './tabs/FamilyDraftTab.vue'
import FamilyTab          from './tabs/FamilyTab.vue'
import DocumentsDraftTab  from './tabs/DocumentsDraftTab.vue'
import DocumentsTab       from './tabs/DocumentsTab.vue'
import CustomFieldsSection from '@/core/components/CustomFieldsSection.vue'

// BYPASS TEMPORAL QA (2026-08-05): la consulta a la API de SUNAT/RENIEC por DNI está devolviendo error
// y bloquea el alta de colaborador. Con el flag en false se saltea la consulta y los campos (nombres,
// apellidos) se completan a mano; el guardado no queda bloqueado. Reactivar poniéndolo en true cuando
// la API externa esté operativa. NO borrar la integración (queryDni sigue intacto).
const SUNAT_LOOKUP_ENABLED = false

export default {
  components: {
    CustomFieldsSection,
    PersonalDataTab,
    ContactTab,
    BankAndPensionsTab,
    AcademicTab,
    FamilyDraftTab,
    FamilyTab,
    DocumentsDraftTab,
    DocumentsTab,
  },

  data() {
    return {
      loading:   true,
      editing:   false,
      activeTab: 'personal',

      // Derechohabientes capturados en el alta (se envían junto con el colaborador). Solo aplica en alta.
      familyDependents: [],
      // Documentos capturados en el alta (buffer). Se suben tras crear el colaborador. Solo aplica en alta.
      documentsDraft: [],
      submitting: false,        // deshabilita "Guardar" durante el guardado + subida de documentos
      uploadProgress: null,     // { current, total } durante la subida secuencial de documentos

      districts:       [],
      nationalityList: [],
      banks:           [],
      pensionEntities: [],

      identifierTypes: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
        Constants.IdentifierType.LIBRETA_MILITAR,
        Constants.IdentifierType.CARNET_MILITAR,
      ],
      genderList: [
        { key: 1, label: 'MASCULINO' },
        { key: 2, label: 'FEMENINO' },
      ],

      form: {
        identifierType:   null,
        identifier:       null,
        name:             null,
        lastNamePaternal: null,
        lastNameMaternal: null,
        birthDate:        null,
        gender:           null,
        maritalStatus:    null,
        nationality:      null,
        bloodType:        null,
        hasDisability:    false,
        disabilityType:   null,
        conadisCertificate: null,
        address:                      null,
        ubigeo:                       null,
        birthUbigeo:                  null,
        personalPhone:                null,
        personalEmail:                null,
        emergencyContact:             null,
        emergencyContactRelationship: null,
        emergencyPhoneNumber:         null,
        bankIdentifierType:   null,
        bankIdentifier:       null,
        bankHolder:           null,
        bankId:               null,
        bankAccount:          null,
        accountType:          null,
        bankInterAccount:     null,
        bankAccountVerified:  false,
        bankAccountVerifiedBy: null,
        salaryCurrency:       'PEN',
        ctsBankId:            null,
        ctsAccount:           null,
        ctsCurrency:          'PEN',
        pensionSystem:      null,
        pensionEntityId:    null,
        cusppCode:          null,
        afpCommissionType:  null,
        fifthCategoryStatus: null,
        educationLevel: null,
        profession:     null,
      },
    }
  },

  validations() {
    return {
      form: {
        identifierType:   { required },
        identifier:       { required, numeric },
        name:             { required },
        lastNamePaternal: { required },
        birthDate:        { required },
        gender:           { required },
        disabilityType:   { required: requiredIf(() => this.form.hasDisability) },
        conadisCertificate: { required: requiredIf(() => this.form.hasDisability) },
        personalPhone:    { numeric, mobile: CustomValidators.mobile },
        emergencyPhoneNumber: { numeric, mobile: CustomValidators.mobile },
        personalEmail:    { email },
        bankIdentifierType: { required: requiredIf(() => !!this.form.bankId) },
        bankIdentifier:     { required: requiredIf(() => !!this.form.bankId) },
        bankHolder:         { required: requiredIf(() => !!this.form.bankId) },
        accountType:        { required: requiredIf(() => !!this.form.bankId) },
        bankAccount:        { required: requiredIf(() => !!this.form.bankId) },
        bankInterAccount:   { numeric, minLength: minLength(20) },
        pensionEntityId:    { required: requiredIf(() => this.form.pensionSystem === 'AFP') },
      },
    }
  },

  watch: {
    '$route.params.id': function () {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.hasDisability': function (val) {
      if (!val) {
        this.form.disabilityType = null
        this.form.conadisCertificate = null
      }
    },
    'form.bankId': function (val) {
      if (!val) {
        this.form.accountType = null
        this.form.bankAccount = null
        this.form.bankInterAccount = null
      }
    },
    'form.name': function () { this.updateBankHolder() },
    'form.lastNamePaternal': function () { this.updateBankHolder() },
    'form.lastNameMaternal': function () { this.updateBankHolder() },
    'form.identifierType': function (val) {
      this.form.bankIdentifierType = val
    },
    'form.identifier': async function (val) {
      this.form.bankIdentifier = val
      if (this.loading) return
      this.$v.form.identifier.$touch()
      if (this.$v.form.identifier.$error) return
      if (
        SUNAT_LOOKUP_ENABLED && // BYPASS TEMPORAL QA (2026-08-05): ver flag arriba. Saltea la consulta a DNI.
        this.form.identifierType === Constants.IdentifierType.DNI.key &&
        val && val.length === 8
      ) {
        try {
          this.$store.dispatch('app/loading', true)
          const resp = (await EmployeeService.queryDni(val)).payload
          this.form.name = resp.name
          this.form.lastNamePaternal = resp.surname
        } catch (ex) {
          Alert.error(ex.errors ? ex.errors.message : 'Error al consultar DNI')
        } finally {
          this.$store.dispatch('app/loading', false)
        }
      }
    },
    'form.bankAccountVerified': function () {
      if (!this.form.bankAccountVerifiedBy) {
        this.form.bankAccountVerifiedBy = this.user.email
      }
    },
  },

  computed: {
    ...mapGetters({ user: 'auth/getUser' }),
    employeeId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null
    },
    // Alta = ruta de creación (sin id). Define qué componente usa la pestaña Familia (borrador vs CRUD).
    isAlta() {
      return !this.employeeId
    },
    // La pestaña Familia se muestra en AMBOS modos (consistencia UI): en alta usa el borrador en memoria,
    // en edición usa el FamilyTab con CRUD en vivo (mismo que el detalle).
    tabItems() {
      return [
        { key: 'personal',  label: 'Datos personales',    icon: 'ri-user-line' },
        { key: 'contact',   label: 'Contacto y domicilio', icon: 'ri-map-pin-line' },
        { key: 'bank',      label: 'Banco y pensiones',   icon: 'ri-bank-card-line' },
        { key: 'academic',  label: 'Académico',            icon: 'ri-graduation-cap-line' },
        { key: 'family',    label: 'Familia',             icon: 'ri-parent-line' },
        { key: 'documents', label: 'Documentos',          icon: 'ri-folder-line' },
        { key: 'custom',    label: this.isAlta ? 'Campos personalizados (guarda primero)' : 'Campos personalizados',
          icon: 'ri-list-settings-line', disabled: this.isAlta },
      ]
    },
    // Texto del botón Guardar: refleja el progreso de subida de documentos en el alta.
    saveLabel() {
      if (this.uploadProgress) {
        return `Subiendo documento ${this.uploadProgress.current} de ${this.uploadProgress.total}...`
      }
      return this.submitting ? 'Guardando...' : 'Guardar'
    },
  },

  methods: {
    updateBankHolder() {
      this.form.bankHolder = [this.form.name, this.form.lastNamePaternal, this.form.lastNameMaternal]
        .filter(Boolean).join(' ')
    },

    // Activa la pestaña que contiene el primer campo con error de validación (en orden personal → contacto
    // → banco), para que el usuario vea qué falta en vez de un fallo silencioso.
    focusFirstErrorTab() {
      const tabFields = {
        personal: ['identifierType', 'identifier', 'name', 'lastNamePaternal', 'birthDate', 'gender',
          'disabilityType', 'conadisCertificate'],
        contact:  ['personalPhone', 'emergencyPhoneNumber', 'personalEmail'],
        bank:     ['bankIdentifierType', 'bankIdentifier', 'bankHolder', 'accountType', 'bankAccount',
          'bankInterAccount', 'pensionEntityId'],
      }
      for (const tab of ['personal', 'contact', 'bank']) {
        const hasError = tabFields[tab].some(f => this.$v.form[f] && this.$v.form[f].$error)
        if (hasError) { this.activeTab = tab; return }
      }
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.focusFirstErrorTab()
        Alert.error('Complete los campos obligatorios marcados en rojo. Los datos bancarios requieren '
          + 'titular, tipo/número de documento y tipo de cuenta cuando hay banco asignado.')
        this.$nextTick(() => {
          const errorEl = document.querySelector('.wz-form-error-msg, .form-error')
          if (errorEl) {
            this.$smoothScroll({ scrollTo: errorEl, updateHistory: false, offset: -50 })
          }
        })
        return
      }

      try {
        this.submitting = true
        this.$store.dispatch('app/loading', true)
        const data = { ...this.form }
        Object.keys(data).forEach(key => {
          if (data[key] === '') data[key] = null
        })
        let response
        if (!this.editing) {
          // Alta: incluir los derechohabientes para crearlos junto con el colaborador (transaccional).
          data.dependents = this.familyDependents
          response = await EmployeeService.save(data)
        } else {
          response = await EmployeeService.update(this.$route.params.id, data)
        }
        if (response.status === 'BAD_REQUEST') {
          Alert.error('Error de validación: revise los campos')
          return
        }
        const savedId = response.payload ? response.payload.id : this.$route.params.id

        // Alta con documentos en buffer: subirlos secuencialmente tras crear el colaborador (Opción A).
        if (!this.editing && this.documentsDraft.length > 0) {
          const failed = await this.uploadDraftDocuments(savedId)
          if (failed.length === 0) {
            Alert.success('Colaborador y documentos guardados correctamente')
            this.$router.push({ name: 'employee-details', params: { id: String(savedId) } })
          } else {
            const names = failed.map(f => f.documentTypeName).join(', ')
            Alert.alert('Documentos pendientes', 'Colaborador creado correctamente. Los siguientes '
              + 'documentos no se pudieron subir: ' + names + '. Complete desde la pestaña Documentos.')
            this.$router.push({ name: 'employee-details', params: { id: String(savedId) }, query: { tab: 'documents' } })
          }
        } else {
          Alert.success('Se almacenó la información correctamente')
          this.$router.push({ name: 'employee-details', params: { id: String(savedId) } })
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.submitting = false
        this.uploadProgress = null
        this.$store.dispatch('app/loading', false)
      }
    },

    // Sube los documentos del buffer (alta) uno por uno contra el colaborador recién creado.
    // No aborta ante un fallo: acumula los fallidos y los devuelve para el mensaje de fallo parcial.
    async uploadDraftDocuments(employeeId) {
      const failed = []
      const total = this.documentsDraft.length
      for (let i = 0; i < total; i++) {
        const doc = this.documentsDraft[i]
        this.uploadProgress = { current: i + 1, total }
        try {
          const formData = new FormData()
          formData.append('employeeId', employeeId)
          formData.append('documentTypeId', doc.documentTypeId)
          formData.append('file', doc.file)
          if (doc.notes) formData.append('notes', doc.notes)
          formData.append('signed', doc.signed)
          await EmployeeDocumentService.upload(formData)
        } catch (ex) {
          console.error('Error subiendo documento', doc.documentTypeName, ex)
          failed.push(doc)
        }
      }
      this.uploadProgress = null
      return failed
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await EmployeeService.get(id)).payload
        this.form = {
          ...payload,
          bankId:          payload.bank          ? payload.bank.id          : null,
          ctsBankId:       payload.ctsBank       ? payload.ctsBank.id       : null,
          pensionEntityId: payload.pensionEntity ? payload.pensionEntity.id : null,
        }
        // Datos legacy: colaboradores con banco asignado (bankId) pero sub-campos vacíos bloqueaban la
        // edición por la validación condicional. Se autocompletan los derivables si están vacíos (no se
        // sobre-escribe lo ya cargado). accountType NO se deriva (Ahorros/Corriente lo elige RRHH).
        if (this.form.bankId) {
          if (!this.form.bankHolder) {
            this.form.bankHolder = [this.form.name, this.form.lastNamePaternal, this.form.lastNameMaternal]
              .filter(Boolean).join(' ')
          }
          if (this.form.bankIdentifierType == null || this.form.bankIdentifierType === '') {
            this.form.bankIdentifierType = this.form.identifierType
          }
          if (!this.form.bankIdentifier) {
            this.form.bankIdentifier = this.form.identifier
          }
        }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'employee-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id)
    else this.loading = false

    try {
      const [districts, countries, banks, pensionEntities] =
        await Promise.all([
          UbigeoService.listDistricts(),
          CountryService.list(),
          BankService.list(),
          PensionEntityService.list(),
        ])

      this.districts       = (districts.payload       || []).map(b => ({ key: b.id, label: b.description }))
      this.nationalityList = (countries.payload        || []).map(c => ({ key: c.id, label: c.description }))
      this.banks           = (banks.payload            || []).map(b => ({ key: b.id, label: b.name }))
      this.pensionEntities = (pensionEntities.payload  || []).map(p => ({ key: p.id, label: `${p.code} - ${p.name}`, code: p.code }))
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar los catálogos')
    }
  },
}
</script>

<style lang="stylus" scoped></style>
